import { Fragment, useState, useCallback, useEffect, useRef } from 'react';
import { ClockIcon } from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import { WithTooltip } from '../../../components/WithTooltip';
import { Mixpanel } from '../../../services/Mixpanel';
import { getUnlimitedPromptPackets } from '../../../services/PacketService';

const HISTORY_LIMIT = 10;
const FETCH_PER_PAGE = 30;
const DESIRED_WIDTH_PX = 640;
const VIEWPORT_PADDING_PX = 8;
const MIN_SPACE_BELOW_PX = 240;

const dedupePromptPackets = (packets, limit) => {
  const seen = new Set();
  const out = [];
  for (const p of packets || []) {
    const raw = p?.input_flags_json?.unlimited_request;
    if (typeof raw !== 'string') continue;
    const prompt = raw.trim();
    if (!prompt || seen.has(prompt)) continue;
    seen.add(prompt);
    out.push({ packet_id: p.id, prompt, timestamp: p.timestamp });
    if (out.length >= limit) break;
  }
  return out;
};

/**
 * Clock-icon toolbar button that opens a dropdown of the user's recent Unlimited prompts.
 * Selecting a prompt invokes `onSelect(promptText)`, which is expected to populate the
 * input, focus the textarea, and re-fire autocomplete (see `handlePromptHistorySelect`
 * in UnlimitedContext). The history list is lazy-fetched on first open and cached.
 *
 * @param {Object} props
 * @param {Function} props.onSelect - Called with the selected prompt text.
 * @param {boolean} [props.disabled] - Whether the button is disabled (e.g. while submitting).
 * @param {Function} [props.requireAuth] - Auth gate; called with a callback when the user clicks.
 */
export const PromptHistoryButton = ({ onSelect, disabled, requireAuth }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [prompts, setPrompts] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const menuRef = useRef(null);
  const [dropUp, setDropUp] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});

  const recalculate = useCallback(() => {
    const rect = menuRef.current?.getBoundingClientRect();
    if (!rect) return;
    const vw = window.innerWidth;
    const width = Math.min(DESIRED_WIDTH_PX, vw - 2 * VIEWPORT_PADDING_PX);
    // Right-align dropdown's right edge to the trigger's right edge, then clamp so
    // the left edge stays >= VIEWPORT_PADDING_PX from the viewport's left edge.
    let leftRelToTrigger = rect.width - width;
    const leftAbs = rect.left + leftRelToTrigger;
    if (leftAbs < VIEWPORT_PADDING_PX) {
      leftRelToTrigger += VIEWPORT_PADDING_PX - leftAbs;
    }
    setDropdownStyle({ left: leftRelToTrigger, width });
    setDropUp(window.innerHeight - rect.bottom < MIN_SPACE_BELOW_PX);
  }, []);

  const fetchHistory = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await getUnlimitedPromptPackets(FETCH_PER_PAGE);
      setPrompts(dedupePromptPackets(response.data?.results, HISTORY_LIMIT));
    } catch (err) {
      console.error('Failed to load prompt history:', err);
      setLoadError("Couldn't load your prompt history.");
      // Leave prompts as null so the next dropdown open retries the fetch
      // instead of permanently caching the failure.
    } finally {
      setIsLoading(false);
    }
  }, []);

  const openDropdown = useCallback(() => {
    recalculate();
    setIsOpen(true);
    Mixpanel.track('Unlimited Prompt History Opened');
    if (prompts === null) fetchHistory();
  }, [recalculate, prompts, fetchHistory]);

  const handleClick = useCallback(() => {
    const action = () => {
      if (isOpen) {
        setIsOpen(false);
      } else {
        openDropdown();
      }
    };
    if (requireAuth) {
      requireAuth(action);
    } else {
      action();
    }
  }, [isOpen, openDropdown, requireAuth]);

  const handleSelect = useCallback(
    (entry, position) => {
      Mixpanel.track('Unlimited Prompt History Selected', {
        position,
        prompt_length: entry.prompt.length,
      });
      setIsOpen(false);
      onSelect(entry.prompt);
    },
    [onSelect]
  );

  // Close on Escape for keyboard accessibility
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  return (
    <div className='relative' ref={menuRef}>
      <WithTooltip
        text={isOpen ? '' : 'Recent prompts'}
        position='bottom'
        anchor='center'
        size='xs'
        showArrow>
        <button
          type='button'
          onClick={handleClick}
          disabled={disabled}
          className={`flex items-center gap-1.5 p-2.5 my-1 rounded-xl transition-colors cursor-pointer ${
            isOpen
              ? 'bg-gray-100 text-gray-700'
              : 'text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed'
          }`}
          aria-label='Show recent prompts'
          aria-expanded={isOpen}
          aria-haspopup='menu'>
          <ClockIcon className='w-5 h-5' />
        </button>
      </WithTooltip>

      {isOpen && (
        <>
          <div className='fixed inset-0 z-40' onClick={() => setIsOpen(false)} />
          <div
            role='menu'
            style={dropdownStyle}
            className={`absolute bg-white rounded-xl shadow-lg border border-gray-200 z-50 ${
              dropUp ? 'bottom-full mb-1' : 'top-full mt-1'
            }`}>
            <div className='px-3 pt-2.5 pb-1.5 text-xs font-normal text-gray-600 uppercase tracking-wide'>
              Recent prompts
            </div>
            <div className='max-h-56 overflow-y-auto overscroll-contain scrollbar-dropdown px-1.5 pb-1.5'>
              {isLoading && (
                <div className='px-3 py-4 text-sm text-gray-500'>
                  <span>Loading…</span>
                </div>
              )}
              {!isLoading && loadError && (
                <div className='px-3 py-4 text-sm text-red-600'>
                  <span>{loadError}</span>
                </div>
              )}
              {!isLoading && !loadError && prompts && prompts.length === 0 && (
                <div className='px-3 py-4 text-sm text-gray-500'>
                  <span>No previous prompts yet</span>
                </div>
              )}
              {!isLoading &&
                !loadError &&
                prompts?.map((entry, index) => (
                  <Fragment key={entry.packet_id}>
                    {index > 0 && (
                      <div className='border-t border-gray-100 mx-3' aria-hidden='true' />
                    )}
                    <button
                      type='button'
                      role='menuitem'
                      onClick={() => handleSelect(entry, index)}
                      className='w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors'>
                      <p className='text-sm text-gray-800 line-clamp-2'>{entry.prompt}</p>
                      <p className='text-xs text-gray-500 mt-0.5'>
                        {formatDistanceToNow(new Date(entry.timestamp), { addSuffix: true })}
                      </p>
                    </button>
                  </Fragment>
                ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
